/**
 * Cliente HTTP da API.
 *
 * - O access token vive apenas em memória (não em localStorage), o que remove
 *   a superfície clássica de roubo de token por XSS.
 * - A sessão é reconstruída por um refresh token httpOnly; um 401 dispara um
 *   único refresh e refaz a requisição original.
 * - Refreshes concorrentes compartilham a mesma promise, então uma rajada de
 *   requisições nunca gera vários refreshes (o que invalidaria a rotação).
 */

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, string>;

  constructor(status: number, code: string, message: string, details?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Primeira mensagem de validação de campo, quando houver. */
  get fieldMessage(): string | null {
    const values = this.details ? Object.values(this.details) : [];
    return values[0] ?? null;
  }
}

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;
let onSessionLost: (() => void) | null = null;

/**
 * Pista de sessão: apenas um marcador booleano, nunca um token. Serve para
 * pular a tentativa de refresh (e o 401 correspondente) em quem nunca logou
 * neste navegador. O refresh token de verdade continua no cookie httpOnly.
 */
const SESSION_HINT = 'painel.session';

export function markSession(active: boolean) {
  try {
    if (active) localStorage.setItem(SESSION_HINT, '1');
    else localStorage.removeItem(SESSION_HINT);
  } catch {
    // Navegação privada ou storage bloqueado: seguimos sem a otimização.
  }
}

export function hasSessionHint(): boolean {
  try {
    return localStorage.getItem(SESSION_HINT) === '1';
  } catch {
    // Sem storage, tentamos o refresh do mesmo jeito — só perdemos o atalho.
    return true;
  }
}

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};
export const getAccessToken = () => accessToken;
export const setSessionLostHandler = (handler: (() => void) | null) => {
  onSessionLost = handler;
};

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Uso interno: impede laço infinito de refresh. */
  skipRetry?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

/**
 * Rotas em que um 401 é a RESPOSTA, não uma sessão expirada.
 *
 * No login, 401 quer dizer "e-mail ou senha incorretos". Tratá-lo como sessão
 * expirada dispara um refresh inútil e, pior, substitui a mensagem do servidor
 * por "Sua sessão expirou" — que numa tela de login não faz sentido nenhum e
 * manda a pessoa procurar o problema no lugar errado.
 */
const AUTH_ANSWER_PATHS = ['/api/auth/login', '/api/auth/refresh', '/api/auth/logout'];

const isAuthAnswer = (path: string) =>
  AUTH_ANSWER_PATHS.some((candidate) => path.startsWith(candidate));

async function parseError(response: Response): Promise<ApiError> {
  let code = 'UNKNOWN';
  let message = `Erro ${response.status}`;
  let details: Record<string, string> | undefined;

  try {
    const payload = (await response.json()) as {
      error?: { code?: string; message?: string; details?: Record<string, string> };
    };
    if (payload.error) {
      code = payload.error.code ?? code;
      message = payload.error.message ?? message;
      details = payload.error.details;
    }
  } catch {
    // Resposta sem corpo JSON — mantém a mensagem genérica.
  }

  return new ApiError(response.status, code, message, details);
}

/** Tenta renovar a sessão. Chamadas simultâneas reaproveitam a mesma promise. */
async function refreshSession(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(buildUrl('/api/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        markSession(false);
        return false;
      }
      const data = (await response.json()) as { accessToken: string };
      accessToken = data.accessToken;
      markSession(true);
      return true;
    } catch {
      return false;
    } finally {
      // Libera o slot no próximo tick, para que quem já espera receba o resultado.
      setTimeout(() => {
        refreshPromise = null;
      }, 0);
    }
  })();

  return refreshPromise;
}

/**
 * Faz a requisição com o token e, num 401, renova a sessão uma única vez.
 * Devolve a resposta já conferida (status 2xx).
 */
async function send(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', body, query, skipRetry = false } = options;
  // Upload de arquivos: o FormData leva o próprio Content-Type multipart, com
  // o boundary — definir à mão quebraria o envio.
  const form = body instanceof FormData ? body : null;

  const headers: Record<string, string> = {};
  if (body !== undefined && !form) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    credentials: 'include',
    ...(body !== undefined ? { body: form ?? JSON.stringify(body) } : {}),
  });

  if (response.status === 401 && !skipRetry && !isAuthAnswer(path)) {
    const renewed = await refreshSession();
    if (renewed) return send(path, { ...options, skipRetry: true });
    accessToken = null;
    markSession(false);
    onSessionLost?.();
    throw new ApiError(401, 'UNAUTHORIZED', 'Sua sessão expirou. Entre novamente.');
  }

  if (!response.ok) throw await parseError(response);
  return response;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => request<T>(path, { query }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Envio de arquivos em multipart/form-data. */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
  /**
   * Arquivo protegido. O token vai no cabeçalho, então um <img src> ou um link
   * direto não passariam; a tela usa o blob como URL local.
   */
  blob: async (path: string): Promise<Blob> => (await send(path, {})).blob(),
  refreshSession,
};
